import { useState, useEffect, useCallback } from 'react';
import { addBreadcrumb, Severity } from '@sentry/react';
import { useFeatureFlag } from 'modules/common/hooks';
import { delay } from 'utils/delay';

const ONLINE_POLLING_INTERVAL = 15 * 1000;
const OFFLINE_POLLING_INTERVAL = 2 * 1000;

async function checkOnline(usePolling: boolean, retry = 1): Promise<boolean> {
  if (usePolling) {
    if (!window.navigator.onLine) {
      return false;
    }

    // avoid CORS errors with a request to your own origin
    const url = new URL(window.location.origin);

    // random value to prevent cached responses
    url.searchParams.set('rand', Math.random().toString(36).substring(2, 15));

    try {
      const response = await fetch(url.toString(), { method: 'HEAD' });
      return response.ok;
    } catch {
      if (retry > 0) {
        retry--;
        await delay(1000);
        return checkOnline(usePolling, retry);
      }
      return false;
    }
  }
  return window.navigator.onLine;
}

export const useOfflineState = (
  onlinePollingInterval = ONLINE_POLLING_INTERVAL,
  offlinePollingInterval = OFFLINE_POLLING_INTERVAL
): any => {
  const [isOnline, setIsOnline] = useState(true);
  const isOfflineImprEnabled = useFeatureFlag('isOfflineImprovementsEnabled');
  const [pollingInterval, setPollingInterval] = useState(onlinePollingInterval);

  const updateOnlineStatus = useCallback(async () => {
    const isOnline = await checkOnline(isOfflineImprEnabled);
    setIsOnline(isOnline);
    setPollingInterval(
      isOnline ? onlinePollingInterval : offlinePollingInterval
    );
    addBreadcrumb({
      type: 'network',
      category: window.navigator.onLine ? 'online' : 'offline',
      level: Severity.Info
    });
  }, [isOfflineImprEnabled, offlinePollingInterval, onlinePollingInterval]);

  useEffect(() => {
    let interval: any = undefined;
    if (isOfflineImprEnabled) {
      interval = setInterval(updateOnlineStatus, pollingInterval);
    }

    window.addEventListener('offline', updateOnlineStatus);
    window.addEventListener('online', updateOnlineStatus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('offline', updateOnlineStatus);
      window.removeEventListener('online', updateOnlineStatus);
    };
  }, [isOfflineImprEnabled, pollingInterval, updateOnlineStatus]);

  return isOnline;
};
